class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
    this.originalButton = null;
    this.stickyButton = null;
    this.productForm = null;
    this.observer = null;
    this.isBuyMore = false;
    this.buyMoreBlock = null;
    this.sectionId = this.dataset.sectionId;
    this.productFormId = this.dataset.productFormId;
  }

  connectedCallback() {
    if (!this.sectionId || !this.productFormId) return;

    this.originalButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    if (!this.originalButton) {
      this.buyMoreBlock = document.querySelector('buy-more-block');
      this.originalButton = this.buyMoreBlock?.querySelector('.buy-more-submit') ?? null;
      this.isBuyMore = !!this.originalButton;
    }

    this.stickyButton = this.querySelector('.sticky-add-to-cart__button');
    this.productForm = document.getElementById(this.productFormId);

    if (!this.originalButton || !this.stickyButton || !this.productForm) {
      this.style.display = 'none';
      return;
    }

    this.initObserver();
    this.stickyButton.addEventListener('click', this.handleStickyButtonClick.bind(this));
    this.initVariantListener();
    this.initPriceListener();
    this.initFooterObserver();
    this.initCartListener();

    if (this.isBuyMore) {
      this.initBuyMoreListener();
      this.updateStickyButtonState();
    }

    this.initStickyImageListener();
    
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) {
      this.hideSticky();
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
    }
    window.removeEventListener('scroll', this.handleScroll);
  }

  initObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && !this.isNearFooter()) {
          this.classList.add('visible');
          document.body.classList.add('sticky-add-to-cart-visible');
        } else if (entry.isIntersecting) {
          this.classList.remove('visible');
          document.body.classList.remove('sticky-add-to-cart-visible');
        }
      });
    }, options);

    this.observer.observe(this.originalButton);
  }

  initFooterObserver() {
    this.handleScroll = () => {
      const isNearFooter = this.isNearFooter();
      const originalButtonRect = this.originalButton.getBoundingClientRect();
      const originalButtonVisible = originalButtonRect.top >= 0 && originalButtonRect.top < window.innerHeight;
      
      if (isNearFooter) {
        this.classList.remove('visible');
        document.body.classList.remove('sticky-add-to-cart-visible');
      } else if (!originalButtonVisible) {
        this.classList.add('visible');
        document.body.classList.add('sticky-add-to-cart-visible');
      } else {
        this.classList.remove('visible');
        document.body.classList.remove('sticky-add-to-cart-visible');
      }
    };

    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  isNearFooter() {
    const scrollHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const clientHeight = window.innerHeight;
    const distanceToBottom = scrollHeight - (scrollTop + clientHeight);
    const stickyHeight = this.offsetHeight || 100;
    
    return distanceToBottom <= stickyHeight;
  }

  handleStickyButtonClick(e) {
    e.preventDefault();
    
    if (this.stickyButton.disabled || this.stickyButton.classList.contains('loading')) {
      return;
    }

    if (this.isBuyMore) {
      this.handleBuyMoreStickyClick();
      return;
    }

    const productFormElement = this.productForm.closest('product-form');
    if (!productFormElement || !productFormElement.form) {
      return;
    }

    const originalSubmitButton = productFormElement.querySelector('[type="submit"]');
    if (!originalSubmitButton || originalSubmitButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.stickyButton.classList.add('loading');
    const spinner = this.stickyButton.querySelector('.loading__spinner');
    const buttonText = this.stickyButton.querySelector('.sticky-add-to-cart__button-text');
    
    if (spinner) spinner.classList.remove('hidden');
    if (buttonText) buttonText.style.opacity = '0';

    originalSubmitButton.click();

    const handleCartUpdate = () => {
      this.stickyButton.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
      if (buttonText) buttonText.style.opacity = '1';
      this.hideSticky();
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-disabled') {
          if (!originalSubmitButton.classList.contains('loading')) {
            handleCartUpdate();
            observer.disconnect();
          }
        }
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (!originalSubmitButton.classList.contains('loading')) {
            handleCartUpdate();
            observer.disconnect();
          }
        }
      });
    });

    observer.observe(originalSubmitButton, {
      attributes: true,
      attributeFilter: ['aria-disabled', 'class']
    });

    setTimeout(() => {
      if (this.stickyButton.classList.contains('loading')) {
        handleCartUpdate();
        observer.disconnect();
      }
    }, 5000);
  }

  initStickyImageListener() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId !== this.sectionId) return;
      this.updateStickyImage();
    });
  }

  updateStickyImage() {
    const stickyMedia = document.getElementById(`StickyMedia-${this.sectionId}`);
    if (!stickyMedia) return;

    const activeImage = document.querySelector(
      `#MediaGallery-${this.sectionId} .product__media-item.is-active img, .product__media-wrapper .product__media-item.is-active img`
    );

    if (!activeImage?.src) return;

    const stickyImage = stickyMedia.querySelector('img');
    if (!stickyImage) return;

    stickyImage.src = activeImage.currentSrc || activeImage.src;
    stickyImage.srcset = activeImage.srcset || '';
    if (activeImage.alt) stickyImage.alt = activeImage.alt;
  }

  initBuyMoreListener() {
    const observer = new MutationObserver(() => {
      this.updateStickyButtonState();
    });

    observer.observe(this.originalButton, {
      attributes: true,
      attributeFilter: ['disabled', 'class']
    });

    this.buyMoreBlock?.querySelectorAll('.buy-more-radio').forEach((radio) => {
      radio.addEventListener('change', () => {
        this.updateStickyButtonState();
      });
    });
  }

  initVariantListener() {
    const variantInput = this.productForm.querySelector('.product-variant-id');
    if (variantInput) {
      const observer = new MutationObserver(() => {
        this.updateStickyButtonState();
      });

      observer.observe(variantInput, {
        attributes: true,
        attributeFilter: ['value', 'disabled']
      });

      variantInput.addEventListener('change', () => {
        this.updateStickyButtonState();
      });
    }
  }

  initPriceListener() {
    if (this.isBuyMore) {
      const summaryElement = this.buyMoreBlock?.querySelector('.buy-more-summary');
      if (summaryElement) {
        const observer = new MutationObserver(() => {
          this.updateStickyPrice();
        });

        observer.observe(summaryElement, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
      this.updateStickyPrice();
      return;
    }

    const priceElement = document.getElementById(`price-${this.sectionId}`);
    if (priceElement) {
      const observer = new MutationObserver(() => {
        this.updateStickyPrice();
      });

      observer.observe(priceElement, {
        childList: true,
        subtree: true
      });
    }
  }

  updateStickyButtonState() {
    if (this.isBuyMore) {
      this.stickyButton.disabled = this.originalButton.disabled;
      const stickyButtonText = this.stickyButton.querySelector('.sticky-add-to-cart__button-text');
      if (stickyButtonText) {
        stickyButtonText.textContent = this.originalButton.textContent.trim();
      }
      return;
    }

    const variantInput = this.productForm.querySelector('.product-variant-id');
    if (!variantInput) return;

    const isDisabled = variantInput.disabled || variantInput.value === '';
    this.stickyButton.disabled = isDisabled;

    const originalButtonText = this.originalButton.querySelector('span');
    const stickyButtonText = this.stickyButton.querySelector('.sticky-add-to-cart__button-text');
    
    if (originalButtonText && stickyButtonText) {
      stickyButtonText.textContent = originalButtonText.textContent.trim();
    }
  }

  handleBuyMoreStickyClick() {
    if (this.originalButton.disabled || this.originalButton.classList.contains('loading')) {
      return;
    }

    this.stickyButton.classList.add('loading');
    const spinner = this.stickyButton.querySelector('.loading__spinner');
    const buttonText = this.stickyButton.querySelector('.sticky-add-to-cart__button-text');

    if (spinner) spinner.classList.remove('hidden');
    if (buttonText) buttonText.style.opacity = '0';

    this.originalButton.click();

    const handleComplete = () => {
      this.stickyButton.classList.remove('loading');
      if (spinner) spinner.classList.add('hidden');
      if (buttonText) buttonText.style.opacity = '1';
      this.updateStickyButtonState();
      this.hideSticky();
    };

    const observer = new MutationObserver(() => {
      if (!this.originalButton.classList.contains('loading')) {
        handleComplete();
        observer.disconnect();
      }
    });

    observer.observe(this.originalButton, {
      attributes: true,
      attributeFilter: ['class']
    });

    setTimeout(() => {
      if (this.stickyButton.classList.contains('loading')) {
        handleComplete();
        observer.disconnect();
      }
    }, 5000);
  }

  updateStickyPrice() {
    if (this.isBuyMore) {
      const stickyPriceElement = document.getElementById(`StickyPrice-${this.sectionId}`);
      const finalEl = this.buyMoreBlock?.querySelector('.buy-more-summary-final');
      const originalEl = this.buyMoreBlock?.querySelector('.buy-more-summary-original');

      if (!stickyPriceElement || !finalEl) {
        return;
      }

      const finalPrice = finalEl.textContent.trim();
      const originalPrice = originalEl?.querySelector('s')?.textContent.trim();
      const hasDiscount = originalEl && !originalEl.classList.contains('hidden') && originalPrice;

      stickyPriceElement.innerHTML = `
        <div class="price price--sticky">
          <div class="price__container">
            ${hasDiscount
              ? `<div class="price__sale">
                  <span class="visually-hidden visually-hidden--inline">Preço regular</span>
                  <span><s class="price-item price-item--regular">${originalPrice}</s></span>
                  <span class="visually-hidden visually-hidden--inline">Preço promocional</span>
                  <span class="price-item price-item--sale price-item--last">${finalPrice}</span>
                </div>`
              : `<div class="price__regular">
                  <span class="visually-hidden visually-hidden--inline">Preço regular</span>
                  <span class="price-item price-item--regular">${finalPrice}</span>
                </div>`
            }
          </div>
        </div>
      `;
      return;
    }

    const priceElement = document.getElementById(`price-${this.sectionId}`);
    const stickyPriceElement = document.getElementById(`StickyPrice-${this.sectionId}`);
    
    if (!priceElement || !stickyPriceElement) {
      return;
    }

    const priceContent = priceElement.querySelector('.price');
    if (!priceContent) {
      return;
    }

    const clonedPrice = priceContent.cloneNode(true);
    stickyPriceElement.innerHTML = '';
    stickyPriceElement.appendChild(clonedPrice);
    
    const priceDiv = stickyPriceElement.querySelector('.price');
    if (priceDiv) {
      priceDiv.classList.add('price--sticky');
    }
  }

  initCartListener() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            if (cartDrawer.classList.contains('active')) {
              this.hideSticky();
            }
          }
        });
      });

      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    const checkCartDrawer = () => {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && drawer.classList.contains('active')) {
        this.hideSticky();
      }
    };

    document.addEventListener('cart:updated', () => {
      this.hideSticky();
      setTimeout(checkCartDrawer, 100);
    });

    document.addEventListener('cart:update', () => {
      this.hideSticky();
      setTimeout(checkCartDrawer, 100);
    });
  }

  hideSticky() {
    this.classList.remove('visible');
    document.body.classList.remove('sticky-add-to-cart-visible');
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}

